import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend, map } from 'underscore';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { makeStyles, Input, shorthands } from '@fluentui/react-components';
import { InvestImageDataService } from '@bingads-webui-campaign/invest-image-data-service';
import { SearchFilled } from '@fluentui/react-icons';
import { PanelImageGrid } from '../panel-image-grid';
import { AdsStudioSpinner } from '../ads-studio-spinner';


const PAGE_SIZE = 20;

const useStyles = makeStyles({
  contentWrapper: {
    paddingTop: '8px',
  },
  searchArea: {
    marginBottom: '16px',
    display: 'flex',
    flexWrap: 'wrap',
  },
  input: {
    width: '260px',
    marginRight: '8px',
    marginBottom: '8px',
  },
  messageBar: {
    marginBottom: '8px',
    backgroundColor: '#EFF6FC',
  },
  dropdown: {
    marginBottom: '8px',
    marginRight: '8px',
    minWidth: 'auto',
  },
  listbox: {
    minWidth: 'auto',
  },
  helpLink: {
    color: '#3073ad',
    ...shorthands.textDecoration('underline'),
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
});

const ASSET_TYPE_IMAGES = 1;

export const InvestImagePanelContent = (props) => {
  const {
    i18n, aid, cid, setSelectedImages, overlayBodyRef, logger,
  } = props;
  const classes = useStyles();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const [images, setImages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const selectedImages = useRef([]);
  const searchQuery = useRef('');
  const skip = useRef(0);
  const hasMore = useRef(true);

  const onInputChange = useCallback((ev, data) => {
    const { value = '' } = data || {};
    searchQuery.current = value;
  }, []);
  const investImageService = useMemo(() => new InvestImageDataService({ cid, aid }), [aid, cid]);

  const getInvestImages = useCallback(options => investImageService.getInvestAssets(options), [investImageService]);


  const setLoadImagesError = (error, creativeId) => {
    let errorMessage = `Could not load invest image assets ${creativeId ? `for creativeId: ${creativeId}` : ''}`;
    if (error && error.responseJSON && error.responseJSON.value && error.responseJSON.value.length > 0) {
      const errorCode = error.responseJSON.value[0].Code;
      const message = error.responseJSON.value[0].Message || errorMessage;
      errorMessage = `${errorCode} ${message}`;
    }
    notificationApi.showWarning(i18n.getString(_TL_('{{errorMessage}}.'), { errorMessage }));
  };

  /* istanbul ignore next */
  const loadImages = useCallback(({ creativeId = searchQuery.current, $skip = skip.current, $query = {} }) => {
    if ($skip === 0) {
      selectedImages.current = [];
      setSelectedImages(selectedImages.current);
      setIsLoadingFirstPage(true);
    } else {
      setIsLoadingMore(true);
    }

    const queryParams = {
      appName: 'CampaignUI',
      assetTypeId: ASSET_TYPE_IMAGES,
      $skip,
      $top: PAGE_SIZE,
    };

    if (creativeId) {
      queryParams.creativeId = creativeId;
    }

    getInvestImages({
      customQueries: extend(queryParams, $query),
    }).then((data) => {
      hasMore.current = data['@odata.count'] > data.value.length + $skip;
      setTotalCount(data['@odata.count'] || 0);

      setImages(prevImages => [
        ...($skip > 0 ? prevImages : []),
        ...map(data.value, (item) => {
          const image = {
            url: item.Url,
            id: item.XandrAssetId,
            description: item.Description,
          };

          return image;
        }),
      ]);

      if ($skip === 0) {
        setIsLoadingFirstPage(false);
      } else {
        setIsLoadingMore(false);
      }
    }).catch((error) => {
      setLoadImagesError(error, creativeId);
      if ($skip === 0) {
        setIsLoadingFirstPage(false);
      } else {
        setIsLoadingMore(false);
      }
    });
  }, [getInvestImages, setSelectedImages]);

  useEffect(() => {
    loadImages({ $skip: 0 });
  }, []);

  useEffect(() => {
    logger.logTrace('Ads Studio - Navigating to view: InvestImages');
    return () => {
      logger.logTrace('Ads Studio - Leaving view: InvestImages');
    };
  }, [logger]);

  /* istanbul ignore next */
  const loadNextPage = useCallback(() => {
    if (hasMore.current) {
      const newSkip = skip.current + PAGE_SIZE;
      skip.current = newSkip;
      loadImages({ $skip: newSkip });
    }
  }, [loadImages]);

  const dataService = useMemo(() => ({
    assets: images,
    totalAssetCount: totalCount,
    loadMoreData: loadNextPage,
  }), [images, loadNextPage, totalCount]);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      skip.current = 0;
      loadImages({
        $skip: 0,
      });
    }
  }, [loadImages]);

  /* istanbul ignore next */
  const onSelectImage = useCallback((imageId) => {
    const originSelectedLength = selectedImages.current.length;
    const newSelectedList = selectedImages.current.filter(({ id }) => id !== imageId);
    if (newSelectedList.length === originSelectedLength) {
      const selectedItem = images.find(({ id }) => id === imageId);
      selectedImages.current.push(selectedItem);
    } else {
      selectedImages.current = newSelectedList;
    }
    setSelectedImages([...selectedImages.current]);
  }, [images, setSelectedImages]);

  return (
    <div className={classNames('invest-content-container', classes.contentWrapper)}>
      <div className={classes.searchArea}>
        <Input
          className={classNames('invest-search-input', classes.input)}
          onChange={onInputChange}
          placeholder={i18n.getString(_TL_('Search by Invest creative Id'))}
          contentBefore={<SearchFilled />}
          onKeyDown={onKeyDown}
        />
      </div>
      { isLoadingFirstPage && !images.length && <AdsStudioSpinner useCustomSize customStyle={{ marginTop: '50px' }} /> }
      {!!images.length && (
        <PanelImageGrid
          i18n={i18n}
          isLoadingMore={isLoadingMore}
          onSelectImage={onSelectImage}
          dataService={dataService}
          overlayBodyRef={overlayBodyRef}
        />
      )}
    </div>
  );
};

InvestImagePanelContent.prototype = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  setSelectedImages: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};

